/**
 * AllerBioturbationPage — Aller 1982 bioturbation porewater diffusion enhancement
 *
 * Benthic macrofauna (chironomids, tubificid worms, oligochaetes) bioirrigate burrows and
 * physically mix the upper 5-10 cm of sediment. This raises the effective porewater diffusion
 * coefficient D_eff far above the molecular value D_m:
 *
 *   D_b = α · ρ_org · N_ind · ξ²       (biodiffusion coefficient, m²/s)
 *   D_eff = D_m + D_b
 *   Enhancement factor F = D_eff / D_m
 *
 * Reference: Aller (1980) Geochim. Cosmochim. Acta 44: 1955-1965;
 *            Aller (1982) Animal-Sediment Relations, Plenum Press, p. 53-102.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Bug, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

export default function AllerBioturbationPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('aller_bioturbation');
  const [density, setDensity] = useState(2500);    // ind/m² macrofauna
  const [mixDepth, setMD] = useState(6);           // cm bioturbated layer
  const [tempC, setTempC] = useState(12);
  const [doBed, setDoBed] = useState(2.5);         // mg/L DO at sediment-water interface

  const R = useMemo(() => {
    // Molecular diffusion of typical solute in porewater (m²/s)
    const D_m = 1.0e-9;
    // Bioturbation activity scaling: linear with density, Q10 ≈ 2 with temp, suppressed by hypoxia
    const tempFactor = Math.pow(2, (tempC - 10) / 10);
    const o2Factor = Math.min(1, doBed / 3);       // <3 mg/L starves macrofauna
    // Aller 1980 empirical: D_b ≈ 5e-12 · density · tempFactor · o2Factor (m²/s)
    const D_b = 5e-12 * density * tempFactor * o2Factor;
    const D_eff = D_m + D_b;
    const enhancement = D_eff / D_m;
    // Effective SOD enhancement (proportional to sqrt of D_eff for steady diffusion)
    const sodEnhancement = Math.sqrt(D_eff / D_m);
    return { D_m, D_b, D_eff, enhancement, sodEnhancement, o2Factor };
  }, [density, mixDepth, tempC, doBed]);

  const fmt = (n: number, d = 2) => isFinite(n) ? n.toFixed(d) : '—';
  const fmtE = (n: number) => isFinite(n) ? n.toExponential(2) : '—';

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={Bug} title="Module — Aller Bioturbation Diffusion Enhancement"
        subtitle="Benthic macrofauna mix porewater, raising effective sediment diffusivity"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('aller_bioturbation')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"><ArrowLeft className="w-4 h-4" /> Modules</button>
        </div>}
      />
      <ModuleConflictBanner moduleId="aller_bioturbation" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Molecular D_m</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmtE(R.D_m)}</div>
          <div className="text-xs text-slate-500 mt-1">m²/s baseline</div>
        </div>
        <div className="rounded-xl border-2 border-sky-700 bg-sky-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-sky-300">Biodiffusion D_b</div>
          <div className="text-2xl font-bold text-sky-200 mt-1">{fmtE(R.D_b)}</div>
          <div className="text-xs text-slate-500 mt-1">m²/s from macrofauna</div>
        </div>
        <div className="rounded-xl border-2 border-amber-700 bg-amber-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-amber-300">Enhancement factor</div>
          <div className="text-2xl font-bold text-amber-200 mt-1">× {fmt(R.enhancement, 1)}</div>
          <div className="text-xs text-slate-500 mt-1">D_eff / D_m</div>
        </div>
        <div className="rounded-xl border-2 border-red-700 bg-red-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-red-300">SOD multiplier</div>
          <div className="text-2xl font-bold text-red-200 mt-1">× {fmt(R.sodEnhancement, 2)}</div>
          <div className="text-xs text-slate-500 mt-1">√(D_eff / D_m) steady-state</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
        <div>
          <label className="text-slate-400 block mb-1">Macrofauna density: <strong className="text-slate-100">{density} /m²</strong></label>
          <input type="range" min={0} max={15000} step={100} value={density} onChange={e => setDensity(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Mixed layer: <strong className="text-slate-100">{mixDepth} cm</strong></label>
          <input type="range" min={1} max={15} step={0.5} value={mixDepth} onChange={e => setMD(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Bed T: <strong className="text-slate-100">{tempC} °C</strong></label>
          <input type="range" min={2} max={22} step={0.5} value={tempC} onChange={e => setTempC(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">DO at interface: <strong className="text-slate-100">{doBed.toFixed(1)} mg/L</strong></label>
          <input type="range" min={0} max={10} step={0.1} value={doBed} onChange={e => setDoBed(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-rb-brand" /> Why this matters for aeration design</div>
        <ul className="list-disc ml-5 space-y-1">
          <li>Aeration restores DO at the sediment-water interface from &lt;1 mg/L (anoxic) to &gt;5 mg/L (oxic).</li>
          <li>This switch allows macrofauna to recolonise — populations of <em>Chironomus</em>, <em>Tubifex</em> recover within weeks-months.</li>
          <li>Recolonised benthos bioirrigate burrows, enhancing porewater diffusion 10-100×.</li>
          <li><strong>Counter-intuitive consequence:</strong> SOD can actually <em>increase</em> after aeration starts (more O₂ reaches deeper layers) — but bed-bound P is also stripped out faster, so the net long-term P-loading drops.</li>
          <li>When DO &lt; 3 mg/L (e.g. during summer surface bloom) the enhancement collapses → bed reverts to molecular-diffusion limited.</li>
        </ul>
        <p className="mt-2">The Boudreau diagenesis module supplies the baseline diffusion. When this module is also enabled, the simulator
        multiplies the Boudreau flux by the enhancement factor F shown above. Designed to be co-active — not a conflict.</p>
        <p className="italic text-slate-500 mt-2">Reference: Aller R.C. (1980) Geochim. Cosmochim. Acta 44: 1955-1965; Aller (1982) Animal-Sediment Relations, Plenum.</p>
      </div>
    </div>
  );
}
